import { mapTargetCTA, mapTargetLink } from '@/App.datalayer';
import { withGTM } from '@/features/shared/HOCs';
import { MuiButtonBase } from '@/features/shared/material-ui';
import SwiperCarousel from '@/features/shared/SwiperCarousel';
import { Theme } from '@/utils/constants';
import { mapLinkToProps } from '@/utils/helpers';
import { isUserInteraction } from '@/utils/swiper.helpers';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import Swiper from 'swiper';
import { SwiperSlide } from 'swiper/react';
import PortalCarouselContentCard from './components/PortalCarouselContentCard';
import {
  LIST_CARD_CAROUSEL_EVENTS,
  LIST_CARD_CAROUSEL_EVENTS_NAMES,
  onDataLayerCardCarousel,
} from './PortalCarousel.datalayer';
import { PortalCarouselProps } from './PortalCarousel.interfaces';
import useCardListSliderStyles from './PortalCarousel.styles';

const PortalCarousel = ({ cards = [], className: overrideClassName }: PortalCarouselProps) => {
  const classes = useCardListSliderStyles();
  const muiTheme = useTheme();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [slider, setSlider] = useState<any>(null);
  const [slideIndex, setSlideIndex] = React.useState<number>();
  const tabTheme = Theme.dark;
  const [triggerCarouselNavClick, setTriggerCarouselNavClick] = React.useState(false);

  const configuration = useRef({
    autoplay: false,
    spaceBetween: isMobile ? 32 : 30,
    slidesPerView: 'auto',
    centerInsufficientSlides: isMobile,
    centeredSlides: isMobile,
    loop: isMobile,
    observer: true,
    loopedSlides: isMobile ? cards.length : undefined,
    pagination: {
      clickable: true,
      el: `.${classes.sliderNavigation}`,
    },
    onSwiper: (swiper: any) => setSlider(swiper),
    onSlideChange: (swiperInstance: Swiper) => {
      const { realIndex } = swiperInstance;
      if (isUserInteraction(swiperInstance)) {
        setTriggerCarouselNavClick(false);
        setSlideIndex(realIndex);

        Promise.resolve().then(() => {
          setTriggerCarouselNavClick(() => true);
        });
      }
    },
  });

  React.useEffect(() => {
    if (!triggerCarouselNavClick) return;

    const dataLayer = {
      event: 'trackEvent-listCardCarouselNavClick',
      event_name: 'list_card_carousel_nav_click',
      title: cards?.[slideIndex || 0]?.cardHeading,
      index_num: String(slideIndex),
    };
    TagManager.dataLayer({ dataLayer });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerCarouselNavClick]);

  const allCards = useMemo(
    () =>
      cards.map((card: CardType, index) => {
        const linkProps = mapLinkToProps({
          href: card?.url,
          openInNewWindow: card?.openInNewWindow,
        });
        return (
          <SwiperSlide
            className={classes.sliderCard}
            key={index}
            data-datalayer-card-index={index}
            data-datalayer-card-title={card.cardHeading}
          >
            <MuiButtonBase className={classes.buttonSlide} {...linkProps}>
              <PortalCarouselContentCard {...card} />
            </MuiButtonBase>
          </SwiperSlide>
        );
      }),
    [cards, classes.sliderCard, classes.buttonSlide],
  );

  useEffect(() => {
    if (slider && !slider.destroyed) {
      Promise.resolve().then(() => {
        slider.update();

        if (!isMobile && slider.realIndex !== 0) {
          slider.slideTo(0);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, slider]);

  return (
    <div ref={containerRef} className={clsx(overrideClassName, 'portal-carousel')}>
      <SwiperCarousel
        sliderNavigationClass="desktop-tab-pagination"
        navigation
        slider={slider}
        classes={classes}
        theme={tabTheme}
        mdTheme={Theme.dark}
        className={classes.swiperCarousel}
        shadowOffset
        shadowTheme={{
          mobile: Theme.dark,
          desktop: Theme.light,
        }}
        {...configuration.current}
      >
        {allCards}
      </SwiperCarousel>
    </div>
  );
};

const PortalCarouselGTMCardCarouselLink = withGTM({
  Component: PortalCarousel,
  onDatalayer: onDataLayerCardCarousel({
    event: LIST_CARD_CAROUSEL_EVENTS.cardClick,
    eventName: LIST_CARD_CAROUSEL_EVENTS_NAMES.cardClick,
  }),
  config: { mapTarget: mapTargetLink },
});
const PortalCarouselGTMCardCarouselCTA = withGTM({
  Component: PortalCarouselGTMCardCarouselLink,
  onDatalayer: onDataLayerCardCarousel({
    event: LIST_CARD_CAROUSEL_EVENTS.ctaClick,
    eventName: LIST_CARD_CAROUSEL_EVENTS_NAMES.ctaClick,
  }),
  config: { mapTarget: mapTargetCTA },
});

export default memo(PortalCarouselGTMCardCarouselCTA);
